import type { ReactNode } from "react";
import { Check, CornerDownRight } from "lucide-react";
import { Modal } from "../Modal";
import type { Account } from "../../services/financialService";
import {
  ACCOUNT_STANDINGS,
  ACCOUNT_TYPES,
  accountPurpose,
  accountStanding,
  PURPOSE_LABELS,
  TYPE_DOTS,
  type ChartRow,
} from "./chartOfAccounts";

const when = (value?: string | null) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "";

const PURPOSE_MEANING: Record<string, string> = {
  posting: "Transactions post straight to this account.",
  summary: "Only totals its subaccounts; nothing posts to it directly.",
  control: "Kept in step by the system from its own records (e.g. what students owe).",
};

/** Requested → Reviewed → Approved, with who and when, for an account request. */
function RequestTrail({ account }: { account: Account }) {
  const stage = account.workflow_status ?? "approved";
  const rejected = stage === "rejected";
  const steps = [
    { label: "Requested", by: account.initiated_by_name, at: account.initiated_at, done: true },
    { label: "Reviewed", by: account.reviewed_by_name, at: account.reviewed_at, done: ["verified", "approved", "posted"].includes(stage) || Boolean(account.reviewed_by_name) },
    {
      label: rejected ? "Rejected" : "Approved",
      by: rejected ? account.rejected_by_name : account.approved_by_name,
      at: rejected ? account.rejected_at : account.approved_at,
      done: rejected || ["approved", "posted"].includes(stage),
    },
  ];
  return (
    <ol className="grid gap-2 sm:grid-cols-3">
      {steps.map((step, index) => {
        const bad = rejected && index === 2;
        return (
          <li key={step.label} className={`rounded-lg border px-3 py-2 ${step.done ? (bad ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50") : "border-gray-200"}`}>
            <p className="flex items-center gap-1.5 text-xs font-semibold text-gray-700">
              <span className={`flex h-4 w-4 items-center justify-center rounded-full text-[10px] ${step.done ? (bad ? "bg-red-500 text-white" : "bg-emerald-500 text-white") : "bg-gray-200 text-gray-600"}`}>
                {step.done ? <Check className="h-3 w-3" /> : index + 1}
              </span>
              {step.label}
            </p>
            <p className="mt-0.5 text-xs text-gray-600">{step.done ? step.by || "Recorded" : "Not yet"}</p>
            {step.done && step.at && <p className="text-[11px] text-gray-400">{when(step.at)}</p>}
          </li>
        );
      })}
    </ol>
  );
}

/**
 * One account in full: where it sits in the chart, what it is for, how it
 * behaves, and how its request went. Edit, submit and delete come from the
 * page as `actions`, since who may do them depends on the viewer.
 */
export function AccountDetailsModal({
  row,
  subaccounts,
  onClose,
  onOpenAccount,
  actions,
}: {
  row: ChartRow | null;
  /** Accounts directly under this one. */
  subaccounts: Account[];
  onClose: () => void;
  onOpenAccount: (account: Account) => void;
  actions?: ReactNode;
}) {
  if (!row) return null;
  const { account, path } = row;
  const type = ACCOUNT_TYPES.find((entry) => entry.type === account.account_type);
  const standing = ACCOUNT_STANDINGS[accountStanding(account)];
  const purpose = accountPurpose(account);
  const behaviours = [
    account.is_contra_account && "Contra account: its balance runs opposite to its type.",
    account.is_cash_equivalent && "Counts as cash, so money received may land here.",
    account.can_pay_from === false && "Payments may not be made from it.",
    account.can_hold_student_receivables && "Fee structures may post what students owe to it.",
  ].filter(Boolean) as string[];
  const facts: [string, ReactNode][] = [
    ["Type", <span className="inline-flex items-center gap-1.5"><span className={`h-1.5 w-1.5 rounded-full ${TYPE_DOTS[account.account_type]}`} />{type?.label}</span>],
    ["Normal balance", account.normal_balance === "debit" ? "Debit" : "Credit"],
    ["Purpose", PURPOSE_LABELS[purpose]],
    ["Postings", String(account.transaction_count ?? 0)],
    ["Statement line", account.statement_category_name ? `${account.statement_section_name ? `${account.statement_section_name} › ` : ""}${account.statement_category_name}` : path.length ? "Follows its parent" : "Not set"],
    ["Status", account.status ? account.status.charAt(0).toUpperCase() + account.status.slice(1) : "—"],
  ];

  return (
    <Modal
      isOpen
      size="2xl"
      title={`${account.code} · ${account.name}`}
      onClose={onClose}
      footer={
        <>
          {actions && <div className="mr-auto flex flex-wrap items-center gap-2">{actions}</div>}
          <button type="button" onClick={onClose} className="btn-outline">Close</button>
        </>
      }
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-start justify-between gap-3 rounded-lg bg-primary-50 px-4 py-3">
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-primary-700">{type?.label} account</p>
            {path.length > 0 && <p className="mt-0.5 text-sm text-gray-600">Under {path.join(" › ")}</p>}
            <p className="mt-1 text-xs text-gray-500">{PURPOSE_MEANING[purpose]}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white px-2.5 py-1 text-xs font-medium text-gray-700">
            <span className={`h-1.5 w-1.5 rounded-full ${standing.dot}`} />
            {standing.label}
          </span>
        </div>

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">What goes here</h4>
          {account.description?.trim() ? (
            <p className="mt-1.5 whitespace-pre-wrap break-words text-sm leading-6 text-gray-800">{account.description}</p>
          ) : (
            <p className="mt-1.5 text-sm text-gray-400">No description yet.</p>
          )}
        </section>

        <dl className="grid gap-x-6 rounded-lg border border-gray-200 px-4 py-1 sm:grid-cols-2">
          {facts.map(([label, value]) => (
            <div key={label} className="flex justify-between gap-4 border-b border-gray-100 py-2 last:border-0">
              <dt className="text-sm text-gray-500">{label}</dt>
              <dd className="break-words text-right text-sm font-medium text-gray-900">{value}</dd>
            </div>
          ))}
        </dl>

        {behaviours.length > 0 && (
          <ul className="space-y-1 rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-700">
            {behaviours.map((line) => <li key={line}>• {line}</li>)}
          </ul>
        )}

        {subaccounts.length > 0 && (
          <section>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Subaccounts ({subaccounts.length})</h4>
            <ul className="mt-2 divide-y divide-gray-100 rounded-lg border border-gray-200">
              {subaccounts.map((child) => (
                <li key={child.id}>
                  <button
                    type="button"
                    onClick={() => onOpenAccount(child)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                  >
                    <CornerDownRight className="h-3.5 w-3.5 text-gray-300" />
                    <span className="font-mono text-xs text-gray-500">{child.code}</span>
                    <span className="truncate text-gray-900">{child.name}</span>
                    <span className="ml-auto text-xs tabular-nums text-gray-500">{child.transaction_count || 0} postings</span>
                  </button>
                </li>
              ))}
            </ul>
          </section>
        )}

        <section>
          <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Approval</h4>
          <div className="mt-2">
            <RequestTrail account={account} />
          </div>
          {account.workflow_status === "returned" && account.return_reason && (
            <p className="mt-2 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
              Sent back{account.returned_by_name ? ` by ${account.returned_by_name}` : ""}: {account.return_reason}
            </p>
          )}
          {account.workflow_status === "rejected" && account.rejection_reason && (
            <p className="mt-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">Rejected: {account.rejection_reason}</p>
          )}
          {account.review_comments?.trim() && (
            <p className="mt-2 text-sm text-gray-700"><span className="text-gray-500">Reviewer's comment:</span> {account.review_comments}</p>
          )}
        </section>
      </div>
    </Modal>
  );
}
